<template>
	<Card
		class="OrganisationCard"
		:featured="organisation?.isFeatured"
		:loading="loading"
		@click="handleCardClick"
		@visible="handleCardVisible"
	>
		<OrganisationHeader
			:organisationName="organisation?.title ?? ''"
			:organisationLogoUrl="organisation?.logoUrl ?? ''"
			:programmeId="organisation?.id ?? ''"
			:reviewRating="organisation?.reviewRating"
			:showLogo="!!organisation?.logoUrl"
			:locations="organisation?.locations ?? []"
			:loading="loading"
			:organisationUrl="organisation?.url ?? ''"
		/>

		<OrganisationRanking
			:rankingDiscipline="organisation?.ranking?.rankingPositionByDiscipline"
			:disciplineName="disciplineName"
			:worldwideRankingPosition="organisation?.ranking?.globalRankingPosition"
			:worldwideRankingPercentage="organisation?.ranking?.globalRankingPercentage"
			:loading="loading"
		/>

		<OrganisationFacts
			:facts="organisation?.standardFacts"
			:highlightedFacts="organisation?.highlightedFacts"
			:loading="loading"
		/>

		<template #right-footer>
			<a
				v-skeleton="{
					loading,
					sizes: {
						Small: { width: '10.75rem', height: '0.75rem' },
						ExtraLarge: { width: '12.563rem', height: '0.75rem' },

					}
				}"
				class="VisitUniversity"
				:href="organisation?.url"
				:target="isMobile ? '_self' : '_blank'"
				data-allow-mismatch="attribute"
				@click.prevent
			>
				Visit University Page
			</a>
		</template>
    </Card>
</template>

<script setup lang="ts">
import { type PropType } from 'vue';
import { useMobile } from '@composables/useMobile';
import { TrackingService } from '@domain/services/TrackingService';
import IPersonalizedOrganisationCard from "@shared/interfaces/dtos/IPersonalizedOrganisationCard";
import Card from '@components/SearchResults/Card/Card.vue';
import OrganisationHeader from './OrganisationHeader/OrganisationHeader.vue';
import OrganisationRanking from './OrganisationRanking/OrganisationRanking.vue';
import OrganisationFacts from './OrganisationFacts/OrganisationFacts.vue';

const { isMobile } = useMobile();
const trackingService = TrackingService.getInstance();

const props = defineProps({
	organisation: {
		type: Object as PropType<IPersonalizedOrganisationCard>,
		required: false,
	},
	position: {
		type: Number,
		required: true,
	},
	loading: {
		type: Boolean,
		default: true,
	},
	disciplineName: {
		type: String,
		required: false,
	},
});

const handleCardClick = () => {
	if (!props.organisation) return;
	if (!props.organisation.url) return;

	trackingService.trackUniversityCardClick(
		Number(props.organisation.id),
		props.position + 1
	);


	//This "trick" avoids changing page before the tracking is fully executed, which could cause the tracking to be lost in some cases.
	setTimeout(() => {
		window.open(`${props.organisation?.url}?ref=search_card`, isMobile.value ? '_self' : '_blank');
	}, 0);
};

const handleCardVisible = () => {
	if (!props.organisation) return;

	trackingService.trackUniversityCardImpression(
		Number(props.organisation.id),
		props.position + 1
	);
};
</script>

<style lang="scss" scoped>
    .OrganisationCard {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;

		cursor: pointer;

		.VisitUniversity {
			color: $Blue-700;
			text-decoration: none;

			@include TextStyle(UiSmallSemibold);

			&:hover {
				text-decoration: underline;
			}

			@include Breakpoint(Large, ExtraLarge) {
				@include TextStyle(UiMediumSemibold);
			}
		}
    }
</style>